import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Input,
  Snackbar,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Switch,
  FormControlLabel,
  Slider,
  Chip
} from '@mui/material';
import {
  CloudUpload,
  Description,
  NavigateNext,
  NavigateBefore,
  Refresh,
  Add,
  Edit,
  Delete,
  DarkMode,
  CompareArrows,
  Lock,
  LockOpen,
  Close,
  Check,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { auth } from '../utils/auth';
import axios from 'axios';
import { saveNote, getNotes } from '../utils/supabase';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { initializeProFeatures, isFeatureAvailable, PRO_FEATURES } from '../utils/proFeatures';
import ProFeaturesDialog from '../components/ProFeaturesDialog';
import { generateContent } from '../utils/ai';

function FlashcardsGenerator() {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [flashcards, setFlashcards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [user, setUser] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [savedFlashcards, setSavedFlashcards] = useState([]);
  const [darkMode, setDarkMode] = useState(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [showFeatureComparison, setShowFeatureComparison] = useState(false);
  
  // Manual flashcard creation states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCardQuestion, setNewCardQuestion] = useState('');
  const [newCardAnswer, setNewCardAnswer] = useState('');
  const [editingCard, setEditingCard] = useState(null);
  
  // Number of flashcards to generate
  const [numFlashcards, setNumFlashcards] = useState(10);
  const [manageCardsDialogOpen, setManageCardsDialogOpen] = useState(false);
  const [learnedStatus, setLearnedStatus] = useState({});
  const [progress, setProgress] = useState(0);

  const { isPro, features, limits } = initializeProFeatures('flashcards');

  useEffect(() => {
    const initUser = async () => {
      try {
        const currentUser = await auth.getCurrentUser();
        setUser(currentUser);
        if (currentUser) {
          try {
            const savedNotes = await getNotes(currentUser.id);
            if (savedNotes && savedNotes.length > 0) {
              try {
                const flashcardNote = savedNotes.find(note => {
                  try {
                    const parsed = JSON.parse(note.content);
                    return Array.isArray(parsed);
                  } catch (e) {
                    return false;
                  }
                });
                
                if (flashcardNote) {
                  const parsedFlashcards = JSON.parse(flashcardNote.content);
                  if (Array.isArray(parsedFlashcards) && parsedFlashcards.length > 0) {
                    setSavedFlashcards(parsedFlashcards);
                    setFlashcards(parsedFlashcards);
                    setSnackbarMessage('Loaded saved flashcards');
                    setSnackbarOpen(true);
                  }
                }
              } catch (parseError) {
                console.error('Error parsing saved flashcards:', parseError);
                setError('Failed to load saved flashcards. The format may be invalid.');
              }
            }
          } catch (error) {
            console.error('Error loading notes:', error);
            setError('Failed to load saved flashcards. Please try again later.');
          }
        }
      } catch (authError) {
        console.error('Authentication error:', authError);
        setError('Failed to authenticate user. Please log in again.');
      }
    };
    initUser();
  }, []);

  useEffect(() => {
    if (!isPro) {
      setNumFlashcards(Math.min(numFlashcards, 10));
    } else {
      setNumFlashcards(Math.min(numFlashcards, 30));
    }
  }, [isPro, numFlashcards]);

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      if (uploadedFile.type === 'application/pdf' || uploadedFile.type === 'text/plain') {
        setFile(uploadedFile);
        setError('');
        if (uploadedFile.size > 1000000) {
          setPdfLoading(true);
          setUploadProgress(0);
          const interval = setInterval(() => {
            setUploadProgress(prev => {
              if (prev >= 100) {
                clearInterval(interval);
                setPdfLoading(false);
                return 100;
              }
              return prev + 10;
            });
          }, 200);
        }
      } else {
        setError('Please upload a PDF or text file');
      }
    }
  };

  const extractTextFromPDF = async (file) => {
    const pdf = await import('pdfjs-dist');
    pdf.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).href;
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdf.getDocument({ data: arrayBuffer });
    const pdfDoc = await loadingTask.promise;
    let extractedText = '';

    try {
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const textContent = await page.getTextContent();
        extractedText += textContent.items.map(item => item.str).join(' ');
      }
      return extractedText;
    } catch (parseError) {
      throw new Error(`PDF parsing failed: ${parseError.message}`);
    }
  };

  const handleAddCard = () => {
    if (!newCardQuestion.trim() || !newCardAnswer.trim()) {
      setError('Both question and answer are required');
      return;
    }
    
    const newCard = {
      question: newCardQuestion,
      answer: newCardAnswer
    };
    
    if (editingCard !== null) {
      const updatedCards = [...flashcards];
      updatedCards[editingCard] = newCard;
      setFlashcards(updatedCards);
      setSnackbarMessage('Flashcard updated successfully!');
    } else {
      setFlashcards([...flashcards, newCard]);
      setSnackbarMessage('Flashcard added successfully!');
    }
    
    setSnackbarOpen(true);
    setDialogOpen(false);
    setNewCardQuestion('');
    setNewCardAnswer('');
    setEditingCard(null);
  };

  const handleEditCard = (index) => {
    setEditingCard(index);
    setNewCardQuestion(flashcards[index].question);
    setNewCardAnswer(flashcards[index].answer);
    setDialogOpen(true);
  };

  const handleDeleteCard = (index) => {
    const updatedCards = [...flashcards];
    updatedCards.splice(index, 1);
    setFlashcards(updatedCards);
    
    if (currentCardIndex >= updatedCards.length) {
      setCurrentCardIndex(Math.max(0, updatedCards.length - 1));
    }
    
    setSnackbarMessage('Flashcard deleted');
    setSnackbarOpen(true);
    setManageCardsDialogOpen(false);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.body.style.backgroundColor = !darkMode ? '#121212' : '#f5f5f5';
  };

  const handleGenerateFlashcards = async () => {
    if (!file && !text.trim()) {
      setError('Please upload a file or enter text');
      return;
    }

    if (!isPro && numFlashcards > limits.cards) {
      setError(`Free users are limited to ${limits.cards} flashcards. Upgrade to Pro for unlimited cards.`);
      return;
    }

    setLoading(true);
    setPdfLoading(false);
    setError('');
    setCurrentCardIndex(0);
    setIsFlipped(false);

    try {
      let content = text;

      if (file) {
        setSnackbarMessage(file.type === 'text/plain' ? 'Processing text file...' : 'Processing PDF file...');
        setSnackbarOpen(true);
        
        if (file.type === 'text/plain') {
          content = await file.text();
        } else if (file.type === 'application/pdf') {
          try {
            const pdf = await import('pdfjs-dist');
            pdf.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).href;
            
            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = pdf.getDocument({ data: arrayBuffer });
            const pdfDoc = await loadingTask.promise;
            let extractedText = '';

            setSnackbarMessage(`PDF has ${pdfDoc.numPages} pages, extracting text...`);
            setSnackbarOpen(true);
            
            for (let i = 1; i <= pdfDoc.numPages; i++) {
              try {
                const page = await pdfDoc.getPage(i);
                const textContent = await page.getTextContent();
                extractedText += textContent.items.map(item => item.str).join(' ');
                
                if (i % 5 === 0 || i === pdfDoc.numPages) {
                  setSnackbarMessage(`Extracted text from ${i}/${pdfDoc.numPages} pages...`);
                  setSnackbarOpen(true);
                }
              } catch (pageError) {
                console.error(`Error extracting text from page ${i}:`, pageError);
              }
            }
            
            if (!extractedText.trim()) {
              throw new Error('Could not extract text from PDF. The file might be scanned or contain only images.');
            }
            
            content = extractedText;
            setSnackbarMessage('PDF text extraction complete!');
            setSnackbarOpen(true);
          } catch (parseError) {
            console.error('PDF parsing error:', parseError);
            throw new Error(`PDF parsing failed: ${parseError.message || 'Unknown error'}`);
          }
        } else {
          throw new Error(`Unsupported file type: ${file.type}. Please upload a PDF or text file.`);
        }
      }

      if (!import.meta.env.VITE_TOGETHER_API_URL || !import.meta.env.VITE_TOGETHER_API_KEY) {
        throw new Error('API configuration is missing. Please check your environment variables.');
      }

      setSnackbarMessage(`Generating ${numFlashcards} flashcards...`);
      setSnackbarOpen(true);

      let jsonFlashcards = [];
      let attempts = 0;
      const maxAttempts = 3;
      
      try {
        // Try to generate exactly the requested number of flashcards
        while (attempts < maxAttempts && jsonFlashcards.length !== numFlashcards) {
          attempts++;
          
          // Step 1: Generate flashcards directly in JSON format with exact count requirement
          const jsonResponse = await generateContent([
            {
              role: 'system',
              content: `You are an expert at creating flashcards from text. You MUST generate EXACTLY ${numFlashcards} flashcards in JSON format. Each flashcard must be an object with "question" and "answer" fields. The output MUST be a valid JSON array with exactly ${numFlashcards} items, no more and no less.`
            },
            {
              role: 'user',
              content: `Create EXACTLY ${numFlashcards} flashcards from this text. The output must be a JSON array with exactly ${numFlashcards} objects, each with "question" and "answer" fields. IMPORTANT: Generate exactly ${numFlashcards} flashcards, not more, not less.\n\nText:\n${content}`
            }
          ], {
            max_completion_tokens: 4000,
            temperature: 0.7
          });

          // Parse the JSON response - clean and sanitize properly
          let cleanContent = jsonResponse.trim();
          
          // Remove markdown code blocks if present
          cleanContent = cleanContent.replace(/^```json\s*/i, '');
          cleanContent = cleanContent.replace(/^```\s*/i, '');
          cleanContent = cleanContent.replace(/```\s*$/g, '');
          
          // Remove any leading/trailing whitespace
          cleanContent = cleanContent.trim();
          
          // Find JSON array in the response (in case there's extra text)
          let jsonMatch = cleanContent.match(/\[[\s\S]*\]/);
          if (jsonMatch) {
            cleanContent = jsonMatch[0];
          }
          
          // Sanitize control characters within JSON string values
          // This function escapes control characters only within string literals
          const sanitizeJsonStrings = (jsonStr) => {
            let result = '';
            let inString = false;
            let escapeNext = false;
            
            for (let i = 0; i < jsonStr.length; i++) {
              const char = jsonStr[i];
              const prevChar = i > 0 ? jsonStr[i - 1] : '';
              
              if (escapeNext) {
                result += char;
                escapeNext = false;
                continue;
              }
              
              if (char === '\\') {
                escapeNext = true;
                result += char;
                continue;
              }
              
              if (char === '"' && prevChar !== '\\') {
                inString = !inString;
                result += char;
                continue;
              }
              
              if (inString) {
                // Inside a string, escape control characters
                if (char === '\n') result += '\\n';
                else if (char === '\r') result += '\\r';
                else if (char === '\t') result += '\\t';
                else if (char === '\f') result += '\\f';
                else if (char === '\b') result += '\\b';
                else if (char === '\v') result += '\\v';
                else if (char.charCodeAt(0) < 32) {
                  // Escape any other control character
                  result += '\\u' + ('0000' + char.charCodeAt(0).toString(16)).slice(-4);
                } else {
                  result += char;
                }
              } else {
                // Outside strings, keep as-is
                result += char;
              }
            }
            
            return result;
          };
          
          const sanitizedContent = sanitizeJsonStrings(cleanContent).trim();

          try {
            jsonFlashcards = JSON.parse(sanitizedContent);
          } catch (initialParseError) {
            console.error("Initial parse error:", initialParseError);
            
            // Try to fix common JSON formatting issues
            let fixedContent = sanitizedContent;
            
            // Fix missing commas between array elements
            // Match: } followed by whitespace and { (missing comma between objects)
            fixedContent = fixedContent.replace(/\}\s*\{/g, '},{');
            // Match: "..." followed by whitespace and { (missing comma after string)
            fixedContent = fixedContent.replace(/("(?:[^"\\]|\\.)*")\s*\{/g, '$1,{');
            // Match: } followed by whitespace and " (missing comma after object)
            fixedContent = fixedContent.replace(/\}\s*("(?:[^"\\]|\\.)*")/g, '},$1');
            // Match: number followed by whitespace and { (missing comma after number)
            fixedContent = fixedContent.replace(/(\d+)\s*\{/g, '$1,{');
            // Match: } followed by whitespace and number (missing comma after object)
            fixedContent = fixedContent.replace(/\}\s*(\d+)/g, '},$1');
            // Match: true/false/null followed by whitespace and { (missing comma)
            fixedContent = fixedContent.replace(/(true|false|null)\s*\{/g, '$1,{');
            // Match: } followed by whitespace and true/false/null (missing comma)
            fixedContent = fixedContent.replace(/\}\s*(true|false|null)/g, '},$1');
            
            // Fix trailing commas before closing brackets/braces
            fixedContent = fixedContent.replace(/,(\s*[}\]])/g, '$1');
            
            // Try to extract JSON array again
            let jsonMatch = fixedContent.match(/\[[\s\S]*\]/);
            if (jsonMatch) {
              try {
                jsonFlashcards = JSON.parse(jsonMatch[0]);
              } catch (secondParseError) {
                console.error("Second parse error:", secondParseError);
                if (attempts >= maxAttempts) {
                  throw new Error(`Could not parse JSON: ${secondParseError.message}`);
                }
                continue; // Retry
              }
            } else {
              if (attempts >= maxAttempts) {
                throw new Error("Could not extract valid JSON array from the response");
              }
              continue; // Retry
            }
          }

          if (!Array.isArray(jsonFlashcards)) {
            if (attempts >= maxAttempts) {
              throw new Error('Response is not an array');
            }
            continue; // Retry
          }

          // Filter valid flashcards
          jsonFlashcards = jsonFlashcards.filter(card => {
            return card && typeof card === 'object' && card.question && card.answer;
          });

          if (jsonFlashcards.length === 0) {
            if (attempts >= maxAttempts) {
              throw new Error('No valid flashcards were found in the response');
            }
            continue; // Retry
          }

          // If we have the exact number, we're done
          if (jsonFlashcards.length === numFlashcards) {
            break;
          }

          // If we have more than requested, trim to exact number
          if (jsonFlashcards.length > numFlashcards) {
            jsonFlashcards = jsonFlashcards.slice(0, numFlashcards);
            break;
          }

          // If we have fewer than requested, try again (unless this is the last attempt)
          if (jsonFlashcards.length < numFlashcards && attempts < maxAttempts) {
            console.warn(`Generated ${jsonFlashcards.length} flashcards, but ${numFlashcards} were requested. Retrying...`);
            continue;
          }
        }

        // Final validation
        if (jsonFlashcards.length !== numFlashcards) {
          if (jsonFlashcards.length > 0) {
            // If we have some flashcards but not the exact number, trim or use what we have
            if (jsonFlashcards.length > numFlashcards) {
              jsonFlashcards = jsonFlashcards.slice(0, numFlashcards);
            }
            setSnackbarMessage(`Generated ${jsonFlashcards.length} flashcards (requested ${numFlashcards})`);
          } else {
            throw new Error(`Failed to generate flashcards after ${maxAttempts} attempts`);
          }
        } else {
          setSnackbarMessage(`Successfully generated exactly ${numFlashcards} flashcards!`);
        }

        setFlashcards(jsonFlashcards);
        setLoading(false);
        setSnackbarOpen(true);
      } catch (err) {
        console.error("AI call error:", err);
        setError(err.message || 'Failed to generate flashcards');
        setLoading(false);
      }
    } catch (err) {
      console.error("Overall flashcard generation error:", err);
      setError(err.message || 'An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleLearnedStatus = (index, status) => {
    setLearnedStatus(prev => ({
      ...prev,
      [index]: status
    }));
    
    if (isPro) {
      const totalCards = flashcards.length;
      const learnedCards = Object.values(learnedStatus).filter(Boolean).length;
      const newProgress = ((learnedCards + (status ? 1 : -1)) / totalCards) * 100;
      setProgress(newProgress);
    }
  };

  const enhanceContent = (text, isQuestion = false) => {
    let enhanced = text;
  
    if (isQuestion) {
      // Format questions
      enhanced = enhanced.replace(/^(.*?\?)/, '## $1');
      enhanced = enhanced.replace(/\((\d+)\s*points?\)/i, '**[$1 points]**');
    } else {
      // Format answers
      enhanced = enhanced.replace(/^(.*?[.!?])(\s|$)/, '### Answer:\n\n$1$2');
      
      // Format key terms and definitions
      const terms = ['definition', 'example', 'note', 'remember', 'key point'];
      const pattern = new RegExp(`\\b(${terms.join('|')})\\b:?`, 'gi');
      enhanced = enhanced.replace(pattern, '**$1:**');
      
      // Format lists
      enhanced = enhanced.replace(/(?:^|\n)[-•]\s+([^\n]+)/gm, '\n* $1');
    }
  
    return enhanced;
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          position: 'relative',
          zIndex: 1
        }}>
          <Typography variant="h2" gutterBottom>
            AI Flashcards Generator
          </Typography>
          <Stack 
            direction="row" 
            spacing={1}
            sx={{
              position: 'relative',
              zIndex: 2
            }}
          >
            <Button
              variant="outlined"
              startIcon={<CompareArrows />}
              onClick={() => setShowFeatureComparison(!showFeatureComparison)}
              sx={{ 
                minWidth: '150px',
                position: 'relative',
                zIndex: 2
              }}
            >
              Compare Features
            </Button>
            <Button
              variant="contained"
              color={isPro ? "error" : "success"}
              startIcon={isPro ? <Lock /> : <LockOpen />}
              onClick={() => setPinDialogOpen(true)}
              sx={{ 
                minWidth: '150px',
                position: 'relative',
                zIndex: 2
              }}
            >
              {isPro ? 'Deactivate Pro' : 'Unlock Pro'}
            </Button>
          </Stack>
        </Box>

        {showFeatureComparison && (
          <Paper sx={{ p: 2, mb: 4 }}>
            <Typography variant="h6" gutterBottom>Feature Comparison</Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={isPro ? <LockOpen /> : <Lock />}
                label="Up to 30 Cards"
                color={isPro ? "success" : "default"}
              />
              <Chip
                icon={isPro ? <LockOpen /> : <Lock />}
                label="Progress Tracking"
                color={isPro ? "success" : "default"}
              />
              <Chip
                icon={isPro ? <LockOpen /> : <Lock />}
                label="Learn Status"
                color={isPro ? "success" : "default"}
              />
              <Chip
                icon={isPro ? <LockOpen /> : <Lock />}
                label="Extended Processing Time"
                color={isPro ? "success" : "default"}
              />
            </Stack>
          </Paper>
        )}

        <Stack spacing={4}>
          {flashcards.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              {isPro && (
                <Box sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Progress: {Math.round(progress)}%
                  </Typography>
                  <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1 }}>
                    <Box
                      sx={{
                        width: `${progress}%`,
                        height: 8,
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                        transition: 'width 0.3s ease-in-out'
                      }}
                    />
                  </Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mr: 2 }}>
                  Card {currentCardIndex + 1} of {flashcards.length}
                </Typography>
                <IconButton 
                  onClick={() => {
                    const shuffled = [...flashcards].sort(() => Math.random() - 0.5);
                    setFlashcards(shuffled);
                    setCurrentCardIndex(0);
                    setIsFlipped(false);
                    setSnackbarMessage('Flashcards shuffled');
                    setSnackbarOpen(true);
                  }}
                  color="primary"
                  title="Shuffle flashcards"
                >
                  <Refresh />
                </IconButton>
                <IconButton 
                  onClick={() => setManageCardsDialogOpen(true)}
                  color="primary"
                  title="Manage flashcards"
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  onClick={() => handleLearnedStatus(currentCardIndex, false)}
                  color={learnedStatus[currentCardIndex] === false ? 'error' : 'default'}
                  title="Mark as Not Learned"
                  sx={{ ml: 1 }}
                >
                  <VisibilityOff />
                </IconButton>
                <IconButton
                  onClick={() => handleLearnedStatus(currentCardIndex, true)}
                  color={learnedStatus[currentCardIndex] === true ? 'success' : 'default'}
                  title="Mark as Learned"
                  sx={{ ml: 1 }}
                >
                  <Visibility />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <IconButton
                  onClick={() => {
                    setCurrentCardIndex((prev) => (prev > 0 ? prev - 1 : flashcards.length - 1));
                    setIsFlipped(false);
                  }}
                  disabled={loading}
                >
                  <NavigateBefore />
                </IconButton>

                <Box sx={{ mx: 2, perspective: '1200px' }}>
                  <motion.div
                    initial={false}
                    animate={{ rotateY: isFlipped ? 180 : 0 }}
                    transition={{ duration: 0.6, ease: 'easeInOut' }}
                    style={{ 
                      position: 'relative', 
                      width: 650, 
                      height: 450, 
                      transformStyle: 'preserve-3d',
                      cursor: 'pointer'
                    }}
                    onClick={() => setIsFlipped(!isFlipped)}
                  >
                    <Card
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        boxShadow: 3,
                        borderRadius: 2,
                        overflow: 'auto',
                        bgcolor: darkMode ? 'background.paper' : '#f8f9fa',
                        color: darkMode ? 'text.primary' : 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CardContent sx={{ p: 4, width: '100%' }}>
                        <Stack spacing={2}>
                          <Typography variant="h5" align="center" sx={{ maxWidth: '90%', overflowWrap: 'break-word', margin: '0 auto' }}>
                            {flashcards[currentCardIndex]?.question || 'No question available'}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        boxShadow: 3,
                        borderRadius: 2,
                        overflow: 'auto',
                        bgcolor: darkMode ? 'primary.dark' : 'primary.light',
                        transform: 'rotateY(180deg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CardContent sx={{ p: 4, width: '100%' }}>
                        <Stack spacing={3} sx={{ width: '90%', margin: '0 auto' }}>
                          <Typography variant="h5" align="center" color="white" gutterBottom>
                            Answer
                          </Typography>
                          <Box sx={{ fontSize: '1.2rem', lineHeight: 1.8, overflowWrap: 'break-word', color: 'white', textAlign: 'center' }}>
                            <ReactMarkdown
                              components={{
                                h2: ({...props}) => <Typography variant="h6" color="primary" gutterBottom {...props} />,
                                h3: ({...props}) => <Typography variant="subtitle1" color="secondary" gutterBottom {...props} />,
                                p: ({...props}) => <Typography variant="body1" sx={{ mb: 1 }} {...props} />,
                                strong: ({...props}) => <Box component="strong" sx={{ color: 'primary.main' }} {...props} />,
                                em: ({...props}) => <Box component="em" sx={{ color: 'secondary.main' }} {...props} />,
                                li: ({...props}) => <Typography component="li" sx={{ ml: 2, mb: 0.5 }} {...props} />
                              }}
                            >
                              {isFlipped ? enhanceContent(flashcards[currentCardIndex].answer) : 
                                           enhanceContent(flashcards[currentCardIndex].question, true)}
                            </ReactMarkdown>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Box>

                <IconButton
                  onClick={() => {
                    setCurrentCardIndex((prev) => (prev < flashcards.length - 1 ? prev + 1 : 0));
                    setIsFlipped(false);
                  }}
                  disabled={loading}
                >
                  <NavigateNext />
                </IconButton>
              </Box>
            </Box>
          )}

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload Document
            </Typography>
            <Stack spacing={2}>
              <Button
                component="label"
                variant="outlined"
                startIcon={pdfLoading ? <CircularProgress size={20} /> : <CloudUpload />}
                sx={{ width: 'fit-content' }}
                disabled={loading || pdfLoading}
              >
                {pdfLoading ? 'Processing...' : 'Upload File'}
                <Input
                  type="file"
                  onChange={handleFileUpload}
                  sx={{ display: 'none' }}
                  accept=".pdf,.txt"
                  disabled={loading || pdfLoading}
                />
              </Button>
              {file && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Selected file: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </Typography>
                  {pdfLoading && (
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <Stack spacing={1} direction="row" alignItems="center">
                        <CircularProgress 
                          variant="determinate" 
                          value={uploadProgress} 
                          size={20} 
                        />
                        <Typography variant="body2" color="text.secondary">
                          {uploadProgress}% processed
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Or Enter Text
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Enter your text here..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={loading}
            />
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Number of Flashcards to Generate
            </Typography>
            <Box sx={{ px: 2, pb: 2 }}>
              <Slider
                value={numFlashcards}
                onChange={(e, newValue) => setNumFlashcards(newValue)}
                min={1}
                max={isPro ? 30 : 10}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                  ...(isPro ? [
                    { value: 20, label: '20' },
                    { value: 30, label: '30' }
                  ] : [])
                ]}
                valueLabelDisplay="auto"
                disabled={loading}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              Selected: {numFlashcards} flashcard{numFlashcards !== 1 ? 's' : ''}
              {!isPro && ' (Free users limited to 10)'}
            </Typography>
          </Paper>

          {error && (
            <Alert 
              severity="error" 
              onClose={() => setError('')}
              sx={{ mb: 2 }}
              variant="filled"
            >
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGenerateFlashcards}
              disabled={loading || (!file && !text.trim())}
              startIcon={loading ? <CircularProgress size={20} /> : <Description />}
            >
              {loading ? 'Generating...' : 'Generate Flashcards'}
            </Button>
          </Box>

          {flashcards.length > 0 && user && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    setLoading(true);
                    if (!Array.isArray(flashcards) || flashcards.length === 0) {
                      throw new Error('No valid flashcards to save');
                    }
                    
                    const validFlashcards = flashcards.filter(card => 
                      card && typeof card === 'object' && card.question && card.answer
                    );
                    
                    if (validFlashcards.length === 0) {
                      throw new Error('No valid flashcards to save');
                    }
                    
                    await saveNote(user.id, JSON.stringify(validFlashcards));
                    setSavedFlashcards(validFlashcards);
                    setSnackbarMessage('Flashcards saved successfully!');
                    setSnackbarOpen(true);
                  } catch (err) {
                    console.error('Save error:', err);
                    setError(err.message || 'Failed to save flashcards. Please try again.');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Flashcards'}
              </Button>
              {savedFlashcards.length > 0 && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setFlashcards(savedFlashcards);
                    setCurrentCardIndex(0);
                    setIsFlipped(false);
                    setSnackbarMessage('Loaded saved flashcards');
                    setSnackbarOpen(true);
                  }}
                  disabled={loading}
                >
                  Load Saved Flashcards
                </Button>
              )}
            </Box>
          )}

          <Box sx={{ position: 'fixed', bottom: 20, right: 20 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => {
                setDialogOpen(true);
                setNewCardQuestion('');
                setNewCardAnswer('');
                setEditingCard(null);
              }}
              sx={{ borderRadius: '50%', p: 2 }}
            >
              Add Card
            </Button>
          </Box>

          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              {editingCard !== null ? 'Edit Flashcard' : 'Add New Flashcard'}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 2 }}>
                <TextField
                  label="Question"
                  fullWidth
                  multiline
                  rows={3}
                  value={newCardQuestion}
                  onChange={(e) => setNewCardQuestion(e.target.value)}
                />
                <TextField
                  label="Answer"
                  fullWidth
                  multiline
                  rows={5}
                  value={newCardAnswer}
                  onChange={(e) => setNewCardAnswer(e.target.value)}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddCard} variant="contained" color="primary">
                {editingCard !== null ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog 
            open={manageCardsDialogOpen} 
            onClose={() => setManageCardsDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Manage Flashcards</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 2 }}>
                {flashcards.map((card, index) => (
                  <Paper key={index} sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box sx={{ width: '80%' }}>
                        <Typography variant="subtitle1" fontWeight="bold">Question:</Typography>
                        <Typography variant="body1" gutterBottom>{card.question}</Typography>
                        <Typography variant="subtitle1" fontWeight="bold">Answer:</Typography>
                        <Typography variant="body1">{card.answer}</Typography>
                      </Box>
                      <Stack direction="column" spacing={1}>
                        <IconButton color="primary" onClick={() => handleEditCard(index)}>
                          <Edit />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDeleteCard(index)}>
                          <Delete />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
                {flashcards.length === 0 && (
                  <Typography variant="body1" align="center">No flashcards available</Typography>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setManageCardsDialogOpen(false)}>Close</Button>
              <Button 
                variant="contained" 
                startIcon={<Add />} 
                onClick={() => {
                  setManageCardsDialogOpen(false);
                  setDialogOpen(true);
                  setNewCardQuestion('');
                  setNewCardAnswer('');
                  setEditingCard(null);
                }}
              >
                Add New
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message={snackbarMessage}
          />
        </Stack>

        {pinDialogOpen && (
          <ProFeaturesDialog
            open={pinDialogOpen}
            onClose={() => setPinDialogOpen(false)}
            component="flashcards"
          />
        )}
      </Container>
    </Box>
  );
}

export default FlashcardsGenerator;

